import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { deactivateZone, listZones, reactivateZone, type Zone } from './reference-data.api';

export type ZonesState = { status: 'loading' } | { status: 'loaded'; zones: Zone[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): ZonesState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The zones tab's list and row actions. Creating and editing happen on the zone's own page (useZoneEditor). */
export function useZones() {
  const [state, setState] = useState<ZonesState>({ status: 'loading' });
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    listZones()
      .then((zones) => {
        if (!cancelled) setState({ status: 'loaded', zones });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /** Re-fetches after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', zones: await listZones() });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  const deactivate = (row: Zone) => rowAction(() => deactivateZone(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`);
  const reactivate = (row: Zone) => rowAction(() => reactivateZone(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`);

  return { state, deactivate, reactivate };
}
